import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivityConfigService } from '../services/activity-config.service';
import {
    ActivityIndicatorConfig,
    Indicator,
    IndicatorValue,
    NOT_APPLICABLE_PROFILE,
    isNotApplicableProfile,
} from '../model/activity-config.model';
import { NotifService } from 'src/app/core/notification/notif.service';

@Component({
    selector: 'edit-profile-dialog',
    templateUrl: './edit-profile-dialog.component.html',
    styleUrls: ['./edit-profile-dialog.component.scss']
})
export class EditProfileDialogComponent {

    /** The entered percentages have to add up to exactly this. */
    private static readonly REQUIRED_TOTAL_PERCENTAGE = 100;


    indicators: Indicator[];
    accountId: number;
    editingProfile: any;
    isEditMode: boolean;
    submitted: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<EditProfileDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly activityConfigService: ActivityConfigService,
        private readonly notifService: NotifService,
    ) {
        this.isEditMode = data.isEditMode ?? true;
        this.indicators = data.indicators || [];
        this.accountId = data.accountId;

        if (this.isEditMode) {
            // Create a deep copy to avoid modifying the original until save
            this.editingProfile = JSON.parse(JSON.stringify(data.profile));
            if (this.isNotApplicable) {
                // Shown as 0 rather than blank: the boxes are read-only, so a blank one would read
                // as "not filled in yet" for a value the user can never supply (FR-1.21).
                this.indicators.forEach(indicator => (this.editingProfile[indicator.code] = 0));
            }
        } else {
            const blank: any = { vulnerabilityType: '' };
            this.indicators.forEach(indicator => (blank[indicator.code] = null));
            this.editingProfile = blank;
        }
    }

    /**
     * Sum of the entered percentages. A blank box counts as 0; negatives are allowed and
     * offset positives, so 110 and -10 together still total 100.
     */
    private calculateTotalPercentage(): number {
        const total = this.indicators.reduce((runningTotal, indicator) => {
            const percentage = this.toPercentage(this.editingProfile[indicator.code]);
            return runningTotal + percentage;
        }, 0);
        // Guard against float drift from decimal entries (e.g. 33.33 + 33.33 + 33.34).
        return Math.round(total * 100) / 100;
    }

    /**
     * NOT APPLICABLE holds no percentages: they are fixed at 0% and read-only here and in the
     * instant calculation card (FR-1.21). Public because the template disables the inputs on it.
     */
    get isNotApplicable(): boolean {
        return isNotApplicableProfile(this.editingProfile.vulnerabilityType);
    }

    private toPercentage(entered: any): number {
        if (entered === null || entered === undefined || entered === '') {
            return 0;
        }
        const percentage = Number(entered);
        return isNaN(percentage) ? 0 : percentage;
    }

    private buildIndicatorValues(): IndicatorValue[] {
        // Box holds a percentage (5 meaning 5%); backend stores/uses the decimal (0.05) -
        // matches every real coefficient verified against the client's own spreadsheet.
        // A blank/untouched indicator means "no coefficient" - same as entering 0.
        return this.indicators.map(indicator => {
            const entered = this.editingProfile[indicator.code];
            const percentage = entered === null || entered === undefined || entered === '' ? 0 : Number(entered);
            return { indicatorId: indicator.id, value: percentage / 100 };
        });
    }

    /**
     * The row handed back to the list, built from the saved config so its status and last
     * updated date match what a reload would show. A blank box means "no coefficient", which
     * is saved as 0 and comes back as 0 on the next load - so normalize it to 0 here too,
     * otherwise the row reads "-" for those indicators until the page is refreshed.
     */
    private savedProfileRow(config: ActivityIndicatorConfig | undefined): any {
        const row = { ...this.editingProfile, status: config?.status, updatedOn: config?.updatedOn };
        this.indicators.forEach(indicator => {
            const entered = row[indicator.code];
            row[indicator.code] = entered === null || entered === undefined || entered === '' ? 0 : Number(entered);
        });
        return row;
    }

    save() {
        const name = (this.editingProfile.vulnerabilityType || '').trim();
        if (!this.isEditMode && !name) {
            this.notifService.error('Vulnerability profile type is required');
            return;
        }

        if (this.isNotApplicable) {
            const entered = this.indicators.filter(
                indicator => this.toPercentage(this.editingProfile[indicator.code]) !== 0);
            if (entered.length > 0) {
                this.notifService.error(
                    `Every indicator percentage must be 0% for the ${NOT_APPLICABLE_PROFILE} profile type`);
                return;
            }
        } else {
            const total = this.calculateTotalPercentage();
            if (total !== EditProfileDialogComponent.REQUIRED_TOTAL_PERCENTAGE) {
                this.notifService.error(
                    `Indicator percentages must total ${EditProfileDialogComponent.REQUIRED_TOTAL_PERCENTAGE}% - currently ${total}%`);
                return;
            }
        }

        this.submitted = true;
        const indicatorValues = this.buildIndicatorValues();

        if (this.isEditMode) {
            this.activityConfigService.updateConfig(this.accountId, this.editingProfile.activityId, indicatorValues).subscribe({
                next: (config) => this.dialogRef.close(this.savedProfileRow(config)),
                error: (error) => this.handleError(error),
            });
        } else {
            this.activityConfigService.createActivity(this.accountId, name).subscribe({
                next: (activity) => {
                    this.editingProfile.activityId = activity.id;
                    this.editingProfile.vulnerabilityType = activity.name;
                    this.activityConfigService.saveConfig(this.accountId, activity.id, indicatorValues).subscribe({
                        next: (config) => this.dialogRef.close(this.savedProfileRow(config)),
                        error: (error) => this.handleError(error),
                    });
                },
                error: (error) => this.handleError(error),
            });
        }
    }

    private handleError(error: any) {
        this.submitted = false;
        this.notifService.error(error?.error?.errors || 'Failed to save. Please try again.');
    }

    cancel() {
        this.dialogRef.close();
    }
}
